/**
 * shared/attachmentsPanel/panelParts.jsx - the Doc Hub panel's small pieces:
 * its colours and styles, size / date formatting, the DocTag label, the link
 * badge and the drop notice. Moved out of AttachmentsPanel.jsx.
 */
import { DOC_TYPE_STYLES } from '../../../utils/attachmentPreview';

const GREEN = '#1B5E20';

function humanSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const LINK_BTN = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 10.5,
  fontWeight: 700,
  color: GREEN,
  fontFamily: 'Montserrat',
};

const FIELD = {
  padding: '7px 10px',
  borderRadius: 6,
  border: '1px solid #D7E3D3',
  fontSize: 11,
  fontFamily: 'Montserrat',
  outline: 'none',
  background: '#fff',
  color: '#1a1a1a',
  boxSizing: 'border-box',
};

/** Small colored label chip: "INVOICE 12345". */
export function DocTag({ type, refNo }) {
  const s = DOC_TYPE_STYLES[type] || DOC_TYPE_STYLES.general;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 9,
        fontSize: 8.5,
        fontWeight: 800,
        letterSpacing: 0.6,
        color: s.color,
        background: s.bg,
        border: `1px solid ${s.border}`,
        fontFamily: 'Montserrat',
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
      {refNo ? ` ${refNo}` : ''}
    </span>
  );
}

/* "This is the file the number opens": solid when a person chose it, quiet
   when the app is choosing (newest PDF) so it is plain that nobody has. */
function LinkBadge({ chosen, children }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 9,
        fontSize: 8.5,
        fontWeight: 800,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
        fontFamily: 'Montserrat',
        color: chosen ? '#fff' : '#3F6B40',
        background: chosen ? GREEN : '#F1F7EF',
        border: `1px solid ${chosen ? GREEN : '#CFE0CA'}`,
      }}
    >
      {chosen ? '\u2713 ' : ''}
      {children}
    </span>
  );
}

/** The "that only came over as a link" message (and its small siblings). */
function DropNotice({ notice, onClose }) {
  if (!notice) return null;
  const box = {
    marginBottom: 10,
    padding: '9px 12px',
    borderRadius: 8,
    fontSize: 10.5,
    lineHeight: 1.5,
    fontFamily: 'Montserrat',
    position: 'relative',
  };
  if (notice.kind === 'busy') {
    return (
      <div role="status" style={{ ...box, color: GREEN, background: '#F1F7EF', border: '1px solid #CFE0CA', fontWeight: 700 }}>
        Bringing the dropped file in…
      </div>
    );
  }
  const close = (
    <button type="button" onClick={onClose} style={{ ...LINK_BTN, position: 'absolute', top: 7, right: 10, color: '#92400E' }}>
      Dismiss
    </button>
  );
  if (notice.kind === 'none') {
    return (
      <div role="alert" style={{ ...box, color: '#92400E', background: '#FFF8E1', border: '1px solid #FFE082' }}>
        {close}
        <b>Nothing to upload in that drop.</b> Drag the file itself (or its attachment), not text.
      </div>
    );
  }
  const links = notice.links || [];
  return (
    <div
      role="alert"
      data-testid="drop-link-notice"
      style={{ ...box, color: '#5D4037', background: '#FFF8E1', border: '1px solid #FFE082' }}
    >
      {close}
      <div style={{ fontWeight: 800, color: '#92400E', marginBottom: 4, paddingRight: 60 }}>
        {links.length === 1 ? 'That came over as a link, not a file' : `${links.length} items came over as links, not files`}
      </div>
      <ul style={{ margin: '0 0 6px', paddingLeft: 16 }}>
        {links.map((l, i) => (
          <li key={`${l.url}-${i}`} style={{ wordBreak: 'break-all' }}>
            <b>{l.name || 'A file'}</b> <span style={{ color: '#8D6E63' }}>from {l.source}</span>
            {l.url && (
              <>
                {' '}
                <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', fontWeight: 700 }}>
                  Open
                </a>
              </>
            )}
          </li>
        ))}
      </ul>
      Teams, the new Outlook and SharePoint only hand the browser a link, and that link needs your Microsoft sign-in. Quick way in:{' '}
      <b>Download</b> it there (or Open, then Download), then drag it in from your Downloads folder. Attachments and emails from the classic
      Outlook app drop straight in.
    </div>
  );
}

export { GREEN, humanSize, formatWhen, LINK_BTN, FIELD, LinkBadge, DropNotice };
